import { api } from './api';
import { getSafeApiUrl } from '../config/environment';

export interface InstallationRequest {
  id?: number;
  userId: number;
  storeId: number;
  scheduledDate: string; // YYYY-MM-DD
  scheduledTime: string; // HH:mm
  status?: string;
  assignedTo?: string;
  assignedToPhone?: string;
  notes?: string;
  specialRequests?: string;
  completedAt?: string;
  createdAt?: string;
  updatedAt?: string;
  /** API에서 Include(Store) 시 내려주는 매장 정보 (목록/상세에서 주소 등 표시용) */
  store?: {
    id?: number;
    name?: string;
    address?: string;
    phoneNumber?: string;
  };
}

export interface CreateInstallationRequestDto {
  scheduledDate: string; // YYYY-MM-DD
  scheduledTime: string; // HH:mm
  specialRequests?: string;
}

export interface UpdateInstallationRequestDto {
  scheduledDate: string;
  scheduledTime: string;
  assignedTo?: string;
  assignedToPhone?: string;
  notes?: string;
  status?: string;
}

export const installationApi = {
  // 세팅 신청 생성
  createInstallationRequest: async (dto: CreateInstallationRequestDto) => {
    console.log('[INSTALLATION API] 세팅 신청 생성:', dto);
    return await api.post<InstallationRequest>('/installation-requests', dto);
  },

  /** 설치 필요 없음 시: InstallationRequest 생성 없이 RegistrationStep만 3(Completed)으로 업데이트. 일반 세팅 신청 시에는 백엔드에서 2(InstallationRequested)로 설정. */
  updateRegistrationStep: async (storeId: number, registrationStep: number) => {
    console.log('[INSTALLATION API] RegistrationStep 업데이트:', storeId, registrationStep);
    return await api.put<void>(`/stores/${storeId}/registration-step`, { registrationStep });
  },

  // 예약 가능한 시간 조회
  getAvailableTimes: async (date: string) => {
    console.log('[INSTALLATION API] 예약 가능한 시간 조회:', date);
    return await api.get<string[]>(`/installation-requests/available-times?date=${date}`);
  },

  // 사용자 세팅 일정 조회
  getInstallationRequestByUserId: async (userId: number) => {
    console.log('[INSTALLATION API] 사용자 세팅 일정 조회:', userId);
    return await api.get<InstallationRequest>(`/installation-requests/user/${userId}`);
  },

  // 세팅 일정 상세 조회
  getInstallationRequest: async (id: number) => {
    console.log('[INSTALLATION API] 세팅 일정 상세 조회:', id);
    return await api.get<InstallationRequest>(`/installation-requests/${id}`);
  },
};

// 관리자용 API
export const adminInstallationApi = {
  // 관리자용: 모든 세팅 일정 목록 조회
  getAllInstallationRequests: async (status?: string) => {
    console.log('[ADMIN INSTALLATION API] 모든 세팅 일정 조회:', status);
    const API_BASE_URL = getSafeApiUrl('GATEWAY');
    const url = status 
      ? `${API_BASE_URL}/installation-requests?status=${status}`
      : `${API_BASE_URL}/installation-requests`;
    const token = localStorage.getItem('adminToken');
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      const errBody = await response.json().catch(() => ({}));
      const msg = (errBody as { error?: string })?.error || '세팅 일정 목록 조회에 실패했습니다.';
      throw new Error(msg);
    }
    const data = await response.json();
    return { data };
  },

  // 관리자용: 날짜별 세팅 일정 조회 (캘린더용)
  getInstallationRequestsByDateRange: async (startDate: string, endDate: string) => {
    console.log('[ADMIN INSTALLATION API] 날짜별 세팅 일정 조회:', startDate, endDate);
    const API_BASE_URL = getSafeApiUrl('GATEWAY');
    const url = `${API_BASE_URL}/installation-requests/calendar?startDate=${startDate}&endDate=${endDate}`;
    const token = localStorage.getItem('adminToken');
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      const errBody = await response.json().catch(() => ({}));
      const msg = (errBody as { error?: string })?.error || '세팅 일정 조회에 실패했습니다.';
      throw new Error(msg);
    }
    const data = await response.json();
    return { data };
  },

  // 관리자용: 세팅 일정 수정 (Identity는 204 No Content 반환)
  updateInstallationRequest: async (id: number, dto: UpdateInstallationRequestDto) => {
    console.log('[ADMIN INSTALLATION API] 세팅 일정 수정:', id, dto);
    const API_BASE_URL = getSafeApiUrl('GATEWAY');
    const url = `${API_BASE_URL}/installation-requests/${id}`;
    const token = localStorage.getItem('adminToken');
    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(dto),
    });
    if (!response.ok) {
      const errBody = await response.json().catch(() => ({}));
      const msg = (errBody as { error?: string })?.error || '세팅 일정 수정에 실패했습니다.';
      throw new Error(msg);
    }
    if (response.status === 204) return { data: null };
    const data = await response.json();
    return { data };
  },

  // 관리자용: 세팅 일정 취소
  cancelInstallationRequest: async (id: number) => {
    console.log('[ADMIN INSTALLATION API] 세팅 일정 취소:', id);
    const API_BASE_URL = getSafeApiUrl('GATEWAY');
    const url = `${API_BASE_URL}/installation-requests/${id}`;
    const token = localStorage.getItem('adminToken');
    const response = await fetch(url, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      const errBody = await response.json().catch(() => ({}));
      const msg = (errBody as { error?: string })?.error || '세팅 일정 취소에 실패했습니다.';
      throw new Error(msg);
    }
  },

  // 관리자용: 세팅 완료 처리 (Identity는 204 No Content 반환)
  completeInstallationRequest: async (id: number, planType?: string) => {
    console.log('[ADMIN INSTALLATION API] 세팅 완료 처리:', id, planType);
    const API_BASE_URL = getSafeApiUrl('GATEWAY');
    const url = `${API_BASE_URL}/installation-requests/${id}/complete`;
    const token = localStorage.getItem('adminToken');
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ planType }),
    });
    if (!response.ok) {
      const errBody = await response.json().catch(() => ({}));
      const msg = (errBody as { error?: string })?.error || '세팅 완료 처리에 실패했습니다.';
      throw new Error(msg);
    }
    if (response.status === 204) return { data: null };
    const data = await response.json();
    return { data };
  },
};
